import { useState } from "react";
import { router } from "@inertiajs/react";
import { DatabaseZap, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import CommandCenterLayout from "@/frontend/layouts/CommandCenterLayout";
import DestinationDialog from "./DestinationDialog";
import OrganizationRouting from "./OrganizationRouting";
import type {
  DestinationRow,
  OrganizationOption,
  RuntimeDestination,
} from "./types";

interface Props {
  destinations: DestinationRow[];
  runtime_destinations: RuntimeDestination[];
  organizations: OrganizationOption[];
  copy: Record<string, string>;
}

function Index({
  destinations,
  runtime_destinations,
  organizations,
  copy,
}: Props) {
  const [editing, setEditing] = useState<DestinationRow | null | undefined>(
    undefined,
  );
  const remove = (destination: DestinationRow) => {
    if (confirm(copy.confirm_delete))
      router.delete(`/cc/forge_destinations/${destination.id}`);
  };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{copy.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{copy.subtitle}</p>
        </div>
        <Button onClick={() => setEditing(null)}>
          <Plus className="size-4" />
          {copy.new}
        </Button>
      </div>
      {destinations.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <DatabaseZap className="mx-auto size-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">{copy.empty}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3">{copy.name}</th>
                <th className="px-4 py-3">{copy.runtime}</th>
                <th className="px-4 py-3">{copy.scope}</th>
                <th className="px-4 py-3">{copy.status}</th>
                <th className="px-4 py-3 text-right">{copy.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {destinations.map((destination) => (
                <tr key={destination.id}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium">{destination.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {destination.kind} · {destination.environment}
                    </p>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {destination.runtime_destination_id || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {destination.global_default
                      ? copy.global
                      : `${destination.organization_ids.length} ${copy.organizations_count}`}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs ${destination.enabled ? "bg-emerald-500/10 text-emerald-700" : "bg-muted text-muted-foreground"}`}
                    >
                      {destination.enabled ? copy.active : copy.inactive}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setEditing(destination)}
                        aria-label={copy.edit}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => remove(destination)}
                        aria-label={copy.delete}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <OrganizationRouting
        organizations={organizations}
        destinations={destinations}
        copy={copy}
      />
      <DestinationDialog
        destination={editing}
        runtimeDestinations={runtime_destinations}
        organizations={organizations}
        copy={copy}
        onClose={() => setEditing(undefined)}
      />
    </div>
  );
}

Index.layout = (page: React.ReactElement) => (
  <CommandCenterLayout>{page}</CommandCenterLayout>
);
export default Index;
